import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";

type RecentValuation = {
  key: string;
  city: string;
  bedrooms: string;
  propertyType: string;
  createdAt: string | Date;
};

type RecentValuationsResponse = {
  items: RecentValuation[];
  total: number;
  sinceDays: number;
};

const MIN_LIVE_THRESHOLD = 3;

function relativeTime(input: string | Date): string {
  const then = new Date(input).getTime();
  if (!Number.isFinite(then)) return "just now";
  const diffSec = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec} sec ago`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export default function RecentValuationsTicker() {
  const { data, isLoading } = useQuery<RecentValuationsResponse>({
    queryKey: ["/api/valuations/recent", { limit: 8 }],
    queryFn: async () => {
      const res = await fetch("/api/valuations/recent?limit=8");
      if (!res.ok) throw new Error("Failed to load recent valuations");
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const items = data?.items ?? [];

  // Track which keys are "fresh" (newly arrived since last poll) so we
  // can play a soft slide-in animation on those entries only.
  const seenKeysRef = useRef<Set<string>>(new Set());
  const [freshKeys, setFreshKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (items.length === 0) return;
    const incoming = new Set(items.map((i) => i.key));
    const fresh = new Set<string>();
    if (seenKeysRef.current.size > 0) {
      items.forEach((i) => {
        if (!seenKeysRef.current.has(i.key)) fresh.add(i.key);
      });
    }
    seenKeysRef.current = incoming;
    if (fresh.size > 0) {
      setFreshKeys(fresh);
      const t = window.setTimeout(() => setFreshKeys(new Set()), 800);
      return () => window.clearTimeout(t);
    }
  }, [items]);

  // Hide / soften when there isn't enough recent activity to credibly
  // claim "live valuations". We wait until the query resolves so the
  // section doesn't flicker on first paint.
  if (!isLoading && items.length < MIN_LIVE_THRESHOLD) {
    return (
      <section
        className="bg-gradient-to-r from-primary/5 via-background to-primary/5 border-y border-border/50 py-4"
        aria-label="Recent property valuations"
        data-testid="recent-valuations-ticker"
        data-state="quiet"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-3">
          <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
          <p
            className="text-sm text-muted-foreground"
            data-testid="ticker-empty-copy"
          >
            Trusted by homeowners across India — be one of the first this
            week to get an instant AI valuation.
          </p>
        </div>
      </section>
    );
  }

  // Duplicate the list so the marquee loops seamlessly.
  const loop = [...items, ...items];

  return (
    <section
      className="bg-gradient-to-r from-primary/5 via-background to-primary/5 border-y border-border/50 py-4"
      aria-label="Recent property valuations"
      data-testid="recent-valuations-ticker"
      data-state="live"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center gap-4">
        <div className="hidden sm:flex items-center gap-2 shrink-0 text-sm font-medium text-foreground">
          <span
            className="relative flex h-2.5 w-2.5"
            data-testid="ticker-live-dot"
          >
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
          <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
          <span>Live valuations</span>
        </div>

        {/* Concise sr-only summary so screen readers get a single
            announcement when the list refreshes, instead of the full
            duplicated marquee track. */}
        <p
          className="sr-only"
          role="status"
          aria-live="polite"
          data-testid="ticker-sr-summary"
        >
          {items.length} recent valuations across India.
          {items[0]
            ? ` Latest: ${items[0].bedrooms} ${items[0].propertyType} in ${items[0].city}, ${relativeTime(items[0].createdAt)}.`
            : ""}
        </p>
        <div
          className="relative flex-1 overflow-hidden ticker-mask"
          aria-hidden="true"
        >
          <div className="flex gap-8 animate-marquee whitespace-nowrap will-change-transform">
            {loop.map((item, idx) => {
              const isFresh = freshKeys.has(item.key);
              return (
                <span
                  key={`${item.key}-${idx}`}
                  className={`text-sm text-muted-foreground inline-flex items-center gap-2 ${
                    isFresh ? "ticker-fresh" : ""
                  }`}
                  data-testid={`ticker-item-${idx}`}
                  data-fresh={isFresh ? "true" : undefined}
                >
                  <span className="font-semibold text-foreground">
                    {item.bedrooms} {item.propertyType}
                  </span>
                  <span>in</span>
                  <span className="text-foreground">{item.city}</span>
                  <span>valued</span>
                  <span className="text-primary font-medium">
                    {relativeTime(item.createdAt)}
                  </span>
                  <span className="text-border mx-2">•</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
